// ProjectSettings — the detail page's Settings tab: a left sub-nav (General /
// Members / Danger Zone) over three panels.
//
// Archive/Restore is WIRED — one shared confirm over POST /projects/update/status
// (the same mutation the listing's bulk archive uses), direction-aware on the
// project's own status. Delete is WIRED too, through the shared
// `ProjectDeleteModal` (DELETE /projects/delete/:id) — the same dialog the
// listing opens; deleting navigates back to the list, since the project this
// page is built on is gone.
//
// General edits a local draft and saves through PATCH /projects/update/:id
// sending ONLY the changed keys, so it can't clobber the fields it doesn't own
// (`icon`, `groups`). Members keeps its OWN draft behind a Save changes /
// Discard footer — the picker and both remove affordances only touch local
// state, and Save PATCHes just the changed `members`/`groups` keys through the
// same endpoint.
//
// Status is its own card (`ProjectStateCard`), last in the General panel and
// below the save footer: it writes the operational `state` column through its
// own endpoint, so it sits outside this draft and neither Save nor Reset
// touches it. Tags are selection-only — attach/detach from the project tag
// library, which is managed from the listing's Tags button.
//
// Permissions: General and Members are EDIT surfaces, so without `edit` they
// render read-only (fields disabled, remove controls and the save footers gone)
// rather than disappearing — a viewer can still read a project's settings.
// Danger Zone is different: it is nothing but write actions, so its sub-nav
// entry is dropped entirely when the user holds neither `edit` nor `delete`.

import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Avatar,
  Button,
  Card,
  Chip,
  ConfirmModal,
  Field,
  Icon,
  IconButton,
  Input,
  MembersPicker,
  SelectDropdown,
  TagsPicker,
  Textarea,
} from '@/components/common';
import {
  useUpdateProjectMutation,
  useUpdateProjectsStatusMutation,
  type ProjectDetail,
  type ProjectPriority,
  type UpdateProjectPayload,
} from '@/api/endpoints/projectsApi';
import { useGetProjectTagsQuery } from '@/api/endpoints/tagsApi';
import { useGetUsersSelectionQuery } from '@/api/endpoints/usersApi';
import { useGetGroupsSelectionQuery } from '@/api/endpoints/groupsApi';
import useModuleAccess, { useCanArchiveRestoreProject } from '@/hooks/useModuleAccess';
import toneOf from '@/utils/avatarTone';
import initialsOf from '@/utils/initials';
import { toDateInput, toIso } from '@/utils/date';
import ProjectDeleteModal from './ProjectDeleteModal';
import ProjectStateCard from './ProjectStateCard';

interface ProjectSettingsProps {
  project: ProjectDetail;
}

type Panel = 'general' | 'members' | 'danger';

const PANELS: { key: Panel; icon: string }[] = [
  { key: 'general', icon: 'sliders-horizontal' },
  { key: 'members', icon: 'users' },
  { key: 'danger', icon: 'triangle-alert' },
];

const PRIORITIES: ProjectPriority[] = ['urgent', 'high', 'medium', 'low'];

/** The editable slice of a project — the draft, and the baseline it diffs against. */
const seedOf = (project: ProjectDetail) => ({
  title: project.name,
  description: project.description,
  priority: project.priority,
  startDate: toDateInput(project.startDate),
  dueDate: toDateInput(project.endDate),
  tagIds: project.tags.map((tag) => tag.id),
});

const sameIds = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|');

const ProjectSettings = ({ project }: ProjectSettingsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [panel, setPanel] = useState<Panel>('general');
  const { canEdit, canDelete } = useModuleAccess('projects');
  // Archiving rides on its own `archive_restore` extra, NOT on `edit` — strictly,
  // with no fallback, so `edit` alone does not grant it.
  const canArchiveRestore = useCanArchiveRestoreProject();

  // The system TEST project's settings hold only Members per the design — its
  // identity/schedule/tags aren't user-managed and it can't be archived/deleted.
  // Otherwise: Danger Zone holds only write actions, so it is dropped outright
  // rather than shown empty. Its visibility tracks the two verbs its rows
  // actually use — archive_restore and delete, NOT edit: gating it on edit would
  // leave a user holding edit alone with a nav entry onto an empty panel.
  // `activePanel` falls back to the first visible panel if the selected one
  // stops being visible — the nav can't reach a hidden panel, but the test
  // project (initial state is 'general') and a permission change while the page
  // is open both land here.
  const visiblePanels = PANELS.filter((item) =>
    project.type === 'test'
      ? item.key === 'members'
      : item.key === 'danger'
        ? canArchiveRestore || canDelete
        : true,
  );
  const activePanel = visiblePanels.some((item) => item.key === panel)
    ? panel
    : (visiblePanels[0]?.key ?? 'general');

  const { data: projectTags = [] } = useGetProjectTagsQuery();
  const { data: users = [] } = useGetUsersSelectionQuery();
  const { data: groups = [] } = useGetGroupsSelectionQuery();
  const [updateProject, { isLoading: saving }] = useUpdateProjectMutation();

  // The users/groups-selection ids are the same ids the project's members and
  // groups carry, so these fill the design's email subline and the group chips'
  // member counts with no extra call (MembersPicker already fetches both).
  const userById = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);
  const groupById = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups]);

  const seed = useMemo(() => seedOf(project), [project]);
  const [draft, setDraft] = useState(seed);
  // Re-seed whenever a fresh project lands (first load, or after a save refetch).
  useEffect(() => setDraft(seed), [seed]);

  const patch = (changes: Partial<typeof seed>) => setDraft((d) => ({ ...d, ...changes }));

  const datesInvalid = !!draft.startDate && !!draft.dueDate && draft.dueDate < draft.startDate;
  const titleMissing = !draft.title.trim();
  const dirty =
    draft.title !== seed.title ||
    draft.description !== seed.description ||
    draft.priority !== seed.priority ||
    draft.startDate !== seed.startDate ||
    draft.dueDate !== seed.dueDate ||
    !sameIds(draft.tagIds, seed.tagIds);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Lifecycle move. `movingStatus` is deliberately its OWN flag: `saving` above
  // belongs to updateProject and drives the General/Members footers, which must
  // not spin while a project is being archived.
  const archived = project.status === 'archived';
  const [updateProjectsStatus, { isLoading: movingStatus }] = useUpdateProjectsStatusMutation();

  // ONE call serves both directions — the row, its copy and this mutation all
  // read off `archived`. The mutation invalidates {Projects, id}, so the detail
  // refetches and the header chip, row direction, delete copy, tile fill and
  // Delete's enabled state all follow with no local status state and no
  // navigation.
  const moveStatus = async () => {
    const status = archived ? 'active' : 'archived';
    try {
      await updateProjectsStatus({ ids: [project.id], status }).unwrap();
      toast.success(
        t(archived ? 'projects_restored_toast' : 'projects_archived_toast', { count: 1 }),
      );
      setConfirmArchive(false);
    } catch {
      // Left open on failure so the user can retry; the toast says why.
      toast.error(t('error_generic'));
    }
  };

  /** One PATCH with only the given keys; the cache invalidation refetches. */
  const save = async (body: UpdateProjectPayload, okMessage: string) => {
    try {
      await updateProject({ id: project.id, body }).unwrap();
      toast.success(okMessage);
      return true;
    } catch {
      toast.error(t('error_generic'));
      return false;
    }
  };

  const saveGeneral = () => {
    if (titleMissing || datesInvalid) return;
    // Diff against the seed — untouched fields stay out of the body entirely.
    const body: UpdateProjectPayload = {};
    if (draft.title !== seed.title) body.title = draft.title.trim();
    if (draft.description !== seed.description) body.description = draft.description.trim();
    if (draft.priority !== seed.priority) body.priority = draft.priority;
    if (draft.startDate !== seed.startDate) body.startDate = toIso(draft.startDate);
    if (draft.dueDate !== seed.dueDate) body.dueDate = toIso(draft.dueDate);
    if (!sameIds(draft.tagIds, seed.tagIds)) body.tags = draft.tagIds;
    void save(body, t('projects_settings_saved'));
  };

  // Members is its own draft with a Save/Discard footer — nothing persists until
  // Save, so the picker and both remove affordances only touch local state.
  const peopleSeed = useMemo(
    () => ({
      members: project.members.map((m) => m.id),
      groups: project.groups.map((g) => g.id),
    }),
    [project],
  );
  const [people, setPeople] = useState(peopleSeed);
  useEffect(() => setPeople(peopleSeed), [peopleSeed]);

  const peopleDirty =
    !sameIds(people.members, peopleSeed.members) || !sameIds(people.groups, peopleSeed.groups);

  // Owner leads the list and can never be removed from their own project —
  // locked in the picker (checked + disabled via `lockedUserIds`) and given no
  // remove button in the list. UI-only: nothing is written into the draft or
  // the PATCH body on the owner's behalf.
  const orderedMembers = useMemo(() => {
    const names = new Map(project.members.map((m) => [m.id, m.name]));
    return [...people.members]
      .sort((a, b) => Number(b === project.owner.id) - Number(a === project.owner.id))
      .map((id) => ({
        id,
        name: userById.get(id)?.name ?? names.get(id) ?? '',
        email: userById.get(id)?.email,
      }));
  }, [people.members, project.members, project.owner.id, userById]);

  const savePeople = () => {
    const body: UpdateProjectPayload = {};
    if (!sameIds(people.members, peopleSeed.members)) body.members = people.members;
    if (!sameIds(people.groups, peopleSeed.groups)) body.groups = people.groups;
    void save(body, t('projects_settings_members_saved'));
  };

  // "3 people · 1 group", the design's summary line.
  const peopleSummary = [
    people.members.length === 1
      ? t('members_person')
      : t('members_people', { count: people.members.length }),
    people.groups.length === 0
      ? ''
      : people.groups.length === 1
        ? t('members_group')
        : t('members_groups_suffix', { count: people.groups.length }),
  ]
    .filter(Boolean)
    .join(' · ');

  const subNav = (
    <Card
      padded={false}
      className='project-settings__nav'
      head={<span className='project-detail__section-title'>{t('projects_tab_settings')}</span>}
    >
      <nav className='project-settings__nav-list' aria-label={t('projects_tab_settings')}>
        {visiblePanels.map((item) => (
          <button
            key={item.key}
            type='button'
            aria-current={activePanel === item.key}
            className={`project-settings__nav-item project-settings__nav-item--${item.key}${
              activePanel === item.key ? ' is-active' : ''
            }`}
            onClick={() => setPanel(item.key)}
          >
            <Icon name={item.icon} size={17} />
            {t(`projects_settings_nav_${item.key}`)}
          </button>
        ))}
      </nav>
    </Card>
  );

  return (
    <div className='project-settings'>
      {subNav}

      <div className='project-settings__panel'>
        {activePanel === 'general' && (
          <>
            <Card
              padded={false}
              head={
                <span className='project-detail__section-title'>
                  <Icon name='folder' size={15} />
                  {t('projects_settings_identity')}
                </span>
              }
            >
              <div className='project-settings__form'>
                <Field
                  label={t('projects_field_name')}
                  error={titleMissing ? t('projects_name_required') : undefined}
                >
                  <Input
                    value={draft.title}
                    error={titleMissing}
                    disabled={!canEdit}
                    placeholder={t('projects_name_placeholder')}
                    onChange={(e) => patch({ title: e.target.value })}
                  />
                </Field>
                <Field label={t('projects_field_desc')}>
                  <Textarea
                    rows={4}
                    value={draft.description}
                    disabled={!canEdit}
                    placeholder={t('projects_desc_placeholder')}
                    onChange={(e) => patch({ description: e.target.value })}
                  />
                </Field>
              </div>
            </Card>

            <Card
              padded={false}
              head={
                <span className='project-detail__section-title'>
                  <Icon name='calendar' size={15} />
                  {t('projects_settings_schedule')}
                </span>
              }
            >
              <div className='project-settings__form'>
                <Field label={t('projects_col_priority')} htmlFor='project-priority'>
                  <SelectDropdown
                    id='project-priority'
                    size='lg'
                    value={draft.priority}
                    disabled={!canEdit}
                    onChange={(next) => patch({ priority: next })}
                    options={PRIORITIES.map((p) => ({
                      value: p,
                      label: t(`projects_priority_${p}`),
                    }))}
                  />
                </Field>
                <div className='project-settings__row'>
                  <Field label={t('projects_col_start')} labelTag='· DD-MM-YYYY'>
                    <Input
                      type='date'
                      iconStart='calendar'
                      disabled={!canEdit}
                      value={draft.startDate}
                      onChange={(e) => patch({ startDate: e.target.value })}
                    />
                  </Field>
                  <Field
                    label={t('projects_col_end')}
                    labelTag='· DD-MM-YYYY'
                    error={datesInvalid ? t('projects_dates_invalid') : undefined}
                  >
                    <Input
                      type='date'
                      iconStart='calendar-check'
                      error={datesInvalid}
                      disabled={!canEdit}
                      value={draft.dueDate}
                      onChange={(e) => patch({ dueDate: e.target.value })}
                    />
                  </Field>
                </div>
              </div>
            </Card>

            <Card
              padded={false}
              head={
                <span className='project-detail__section-title'>
                  <Icon name='tag' size={15} />
                  {t('projects_col_tags')}
                </span>
              }
            >
              {/* Selection-only: attach/detach from the library, no inline creation. */}
              <div className='project-settings__form'>
                {draft.tagIds.length > 0 && (
                  <div className='project-settings__tag-chips'>
                    {draft.tagIds.map((id) => {
                      const title = projectTags.find((tag) => tag.id === id)?.title ?? id;
                      return (
                        <Chip key={id} variant='neutral' iconStart='tag'>
                          {title}
                          {canEdit && (
                            <button
                              type='button'
                              className='project-settings__tag-remove'
                              aria-label={t('unit_tag_remove', { tag: title })}
                              onClick={() =>
                                patch({ tagIds: draft.tagIds.filter((x) => x !== id) })
                              }
                            >
                              <Icon name='x' size={12} />
                            </button>
                          )}
                        </Chip>
                      );
                    })}
                  </div>
                )}
                {projectTags.length === 0 ? (
                  <span className='project-settings__hint'>{t('projects_tags_none')}</span>
                ) : (
                  <TagsPicker
                    tags={projectTags}
                    selectedIds={draft.tagIds}
                    disabled={!canEdit}
                    onChange={(ids) => patch({ tagIds: ids })}
                    placeholder={t('projects_tags_pick')}
                  />
                )}
              </div>
            </Card>

            {canEdit && (
              <div className='project-settings__actions'>
                <Button
                  variant='secondary'
                  onClick={() => setDraft(seed)}
                  disabled={!dirty || saving}
                >
                  {t('projects_settings_reset')}
                </Button>
                <Button
                  iconStart='check'
                  onClick={saveGeneral}
                  loading={saving}
                  disabled={!dirty || titleMissing || datesInvalid}
                >
                  {t('projects_settings_save')}
                </Button>
              </div>
            )}

            {/* Last, BELOW the footer on purpose: it saves itself through its
                own endpoint, so nothing should read as covered by Save. */}
            <ProjectStateCard project={project} />
          </>
        )}

        {activePanel === 'members' && (
          <>
            <Card
              padded={false}
              head={
                <div className='project-settings__members-head'>
                  <span className='project-detail__section-title'>
                    <Icon name='users' size={15} />
                    {t('projects_detail_members')}
                  </span>
                  <b className='project-settings__members-count'>{peopleSummary}</b>
                  {canEdit && (
                    <Button
                      variant='secondary'
                      iconStart='user-plus'
                      aria-expanded={pickerOpen}
                      onClick={() => setPickerOpen((o) => !o)}
                    >
                      {t('projects_settings_add_member')}
                    </Button>
                  )}
                </div>
              }
            >
              {/* The Add member button reveals the shared picker; it edits the
                  local draft, so nothing is sent until Save changes. */}
              {pickerOpen && (
                <div className='project-settings__picker'>
                  <MembersPicker
                    selectedUserIds={people.members}
                    selectedGroupIds={people.groups}
                    lockedUserIds={[project.owner.id]}
                    onChangeUsers={(ids) => setPeople((p) => ({ ...p, members: ids }))}
                    onChangeGroups={(ids) => setPeople((p) => ({ ...p, groups: ids }))}
                    disabled={saving}
                  />
                </div>
              )}

              {people.groups.length > 0 && (
                <div className='project-settings__group-block'>
                  <p className='project-settings__block-label'>
                    {t('projects_settings_groups_with_access')}
                  </p>
                  <div className='project-settings__groups'>
                    {people.groups.map((id) => {
                      const group = groupById.get(id);
                      const name =
                        group?.name ?? project.groups.find((g) => g.id === id)?.name ?? id;
                      return (
                        <span key={id} className='project-settings__group'>
                          <Icon name='users' size={13} />
                          <span className='project-settings__group-name'>{name}</span>
                          {group && (
                            <span className='project-settings__group-count'>
                              {group.membersCount}
                            </span>
                          )}
                          {canEdit && (
                            <button
                              type='button'
                              className='project-settings__group-remove'
                              aria-label={t('projects_settings_remove_group', { name })}
                              disabled={saving}
                              onClick={() =>
                                setPeople((p) => ({
                                  ...p,
                                  groups: p.groups.filter((x) => x !== id),
                                }))
                              }
                            >
                              <Icon name='x' size={14} />
                            </button>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {orderedMembers.length === 0 ? (
                <p className='project-detail__empty'>{t('projects_detail_no_members')}</p>
              ) : (
                <>
                  <p className='project-settings__block-label project-settings__block-label--people'>
                    {t('projects_settings_people')}
                  </p>
                  <ul className='project-settings__members'>
                    {orderedMembers.map((member) => {
                      const isOwner = member.id === project.owner.id;
                      return (
                        <li key={member.id} className='project-settings__member'>
                          <Avatar
                            initials={initialsOf(member.name)}
                            tone={toneOf(member.name)}
                            size={38}
                          />
                          <span className='project-settings__member-text'>
                            <span className='project-settings__member-name'>
                              {member.name || '—'}
                              {isOwner && (
                                <Chip variant='success' iconStart='crown'>
                                  {t('projects_detail_owner_badge')}
                                </Chip>
                              )}
                            </span>
                            {member.email && (
                              <span className='project-settings__member-email'>{member.email}</span>
                            )}
                          </span>
                          {!isOwner && canEdit && (
                            <IconButton
                              icon='user-minus'
                              className='project-settings__member-remove'
                              aria-label={t('projects_settings_remove_member', {
                                name: member.name,
                              })}
                              disabled={saving}
                              onClick={() =>
                                setPeople((p) => ({
                                  ...p,
                                  members: p.members.filter((x) => x !== member.id),
                                }))
                              }
                            />
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </Card>

            {canEdit && (
              <div className='project-settings__actions'>
                <Button
                  variant='secondary'
                  onClick={() => setPeople(peopleSeed)}
                  disabled={!peopleDirty || saving}
                >
                  {t('projects_settings_discard')}
                </Button>
                <Button
                  iconStart='check'
                  onClick={savePeople}
                  loading={saving}
                  disabled={!peopleDirty}
                >
                  {t('projects_settings_save')}
                </Button>
              </div>
            )}
          </>
        )}

        {activePanel === 'danger' && (
          <Card
            padded={false}
            className='project-settings__danger'
            head={
              <span className='project-detail__section-title'>
                <Icon name='triangle-alert' size={15} />
                {t('projects_settings_nav_danger')}
              </span>
            }
          >
            {/* Each row is gated whole, not just its button: the tile and copy
                exist only to introduce the action, so a row without one is dead
                weight. */}
            {canArchiveRestore && (
              <div className='project-settings__danger-row'>
                {/* The tile stays pale-red in BOTH directions per the design —
                    the whole panel reads as the danger zone regardless. */}
                <span className='project-settings__danger-tile project-settings__danger-tile--warn'>
                  <Icon name={archived ? 'archive-restore' : 'archive'} size={20} />
                </span>
                <span className='project-settings__danger-text'>
                  <b>
                    {t(
                      archived
                        ? 'projects_settings_restore_title'
                        : 'projects_settings_archive_title',
                    )}
                  </b>
                  {t(
                    archived ? 'projects_settings_restore_body' : 'projects_settings_archive_body',
                  )}
                </span>
                <Button
                  variant='secondary'
                  iconStart={archived ? 'archive-restore' : 'archive'}
                  disabled={movingStatus}
                  onClick={() => setConfirmArchive(true)}
                >
                  {t(archived ? 'projects_action_restore' : 'projects_action_archive')}
                </Button>
              </div>
            )}
            {canDelete && (
              <div className='project-settings__danger-row'>
                {/* Solid red only once the project is archived — which is
                    exactly when Delete becomes usable, so a dead row stays
                    visually quiet. */}
                <span
                  className={`project-settings__danger-tile${
                    archived ? ' project-settings__danger-tile--solid' : ''
                  }`}
                >
                  <Icon name='trash-2' size={20} />
                </span>
                <span className='project-settings__danger-text'>
                  <b>{t('projects_settings_delete_title')}</b>
                  {/* Not-archived explains why the button is dead; archived
                      describes what deleting actually does. */}
                  {t(
                    archived
                      ? 'projects_settings_delete_body_archived'
                      : 'projects_settings_delete_body',
                  )}
                </span>
                <Button
                  variant='danger'
                  iconStart='trash-2'
                  disabled={!archived}
                  onClick={() => setConfirmDelete(true)}
                >
                  {t('button_delete')}
                </Button>
              </div>
            )}
          </Card>
        )}
      </div>

      {/* ONE direction-aware confirm for both archive and restore. It stays open
          with a loading state until the mutation settles (moveStatus closes it
          on success), rather than the old close-then-fire. */}
      <ConfirmModal
        open={confirmArchive}
        onClose={() => setConfirmArchive(false)}
        onConfirm={() => void moveStatus()}
        tone={archived ? 'brand' : 'warning'}
        icon={archived ? 'archive-restore' : 'archive'}
        title={t(archived ? 'projects_settings_restore_title' : 'projects_settings_archive_title')}
        description={
          // Bold mid-sentence, so it goes through Trans (the
          // forms_builder_props_otp_message_hint idiom). Named components rather
          // than positional <1> indices: two bold spans per string. Shared with
          // the listing's confirm — the copy names no project (you are already
          // on its page here, and the listing can be moving a selection).
          <Trans
            i18nKey={archived ? 'projects_restore_confirm_body' : 'projects_archive_confirm_body'}
            count={1}
            components={{ b: <strong /> }}
          />
        }
        confirmLabel={t(
          archived ? 'projects_settings_restore_title' : 'projects_settings_archive_title',
        )}
        confirmVariant={archived ? 'primary' : 'danger'}
        confirmIcon={archived ? 'archive-restore' : 'archive'}
        loading={movingStatus}
      />

      {/* The module's one delete dialog — the same one the listing opens. It
          owns the DELETE call; this page only has to leave, since the project
          it renders no longer exists. */}
      <ProjectDeleteModal
        project={confirmDelete ? project : null}
        onClose={() => setConfirmDelete(false)}
        onDeleted={() => navigate('/projects')}
      />
    </div>
  );
};

export default ProjectSettings;
